import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { INK, FG_2, PURPLE } from '../sections/theme.js';
import { CtaButton } from '../sections/CtaButton.jsx';
import { ArrowIcon } from '../sections/ArrowIcon.jsx';
import { useLocalizedPath } from '../../hooks/useLocalizedPath.jsx';
import { SIGNUP_URL } from '../../constants.js';
import { TrackedEvent } from '../../utils/analytics.js';

const cardSx = {
    background: '#fff',
    border: '1px solid #ededed',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 8px 24px -18px rgba(20,4,77,.22)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    transition: 'transform .18s ease, box-shadow .18s ease',
    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 22px 46px -22px rgba(20,11,61,.28)' },
};

const TOP_CARDS = [
    { key: 'templatemgr', fit: 'cover' },
    { key: 'whitelabel', fit: 'contain', bg: '#F3F2FE' },
    { key: 'bulk', fit: 'contain', bg: '#EDF4FE' },
    { key: 'links', fit: 'cover' },
    { key: 'pdf', fit: 'cover' },
    { key: 'share', fit: 'cover' },
];

const BOTTOM_CARDS = ['security', 'hosting'];

// Uniform image height across all cards so every illustration lines up.
const IMG_H = { xs: 150, md: 156 };

function ScaleCard({ cardKey, fit = 'cover', bg, media }) {
    const { t } = useTranslation();
    return (
        <Box sx={cardSx}>
            <Box sx={{ ...(bg ? { background: bg } : {}), ...media }}>
                <Box
                    component="img"
                    src={`/images/home/scale-${cardKey}.webp`}
                    alt={t(`home.scale.cards.${cardKey}.title`)}
                    loading="lazy"
                    sx={{ width: '100%', height: '100%', objectFit: fit, display: 'block' }}
                />
            </Box>
            <Box sx={{ p: 3 }}>
                <Typography
                    component="h3"
                    sx={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2, color: INK, mb: 1.25 }}
                >
                    {t(`home.scale.cards.${cardKey}.title`)}
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 400, lineHeight: 1.55, color: FG_2 }}>
                    {t(`home.scale.cards.${cardKey}.desc`)}
                </Typography>
            </Box>
        </Box>
    );
}

export default function PlatformScale() {
    const { t } = useTranslation();
    const { prices } = useLocalizedPath();

    return (
        <Box component="section" sx={{ background: '#fff', pt: { xs: 6, md: 9 }, pb: { xs: 6, md: 8 } }}>
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', maxWidth: 680, mx: 'auto', mb: 5.5 }}>
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: { xs: '1.875rem', md: '2.375rem' },
                            fontWeight: 700,
                            lineHeight: 1.15,
                            letterSpacing: '-0.02em',
                            color: INK,
                            mb: 1.5,
                        }}
                    >
                        {t('home.scale.title')}
                    </Typography>
                    <Typography sx={{ fontSize: '1.0625rem', fontWeight: 400, color: FG_2, lineHeight: 1.55 }}>
                        {t('home.scale.desc')}
                    </Typography>
                </Box>

                <Grid container spacing={2.75} sx={{ mb: 2.75 }}>
                    {TOP_CARDS.map((card) => (
                        <Grid key={card.key} size={{ xs: 12, sm: 6, md: 4 }}>
                            <ScaleCard cardKey={card.key} fit={card.fit} bg={card.bg} media={{ height: IMG_H }} />
                        </Grid>
                    ))}
                </Grid>

                <Grid container spacing={2.75} sx={{ mb: 5 }}>
                    {BOTTOM_CARDS.map((key) => (
                        <Grid key={key} size={{ xs: 12, md: 6 }}>
                            <ScaleCard cardKey={key} media={{ height: IMG_H }} />
                        </Grid>
                    ))}
                </Grid>

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={3}
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CtaButton href={prices()} data-track={TrackedEvent.SCHEDULE_DEMO}>
                        {t('home.scale.cta_demo')}
                    </CtaButton>
                    <Box
                        component="a"
                        href={SIGNUP_URL}
                        data-track={TrackedEvent.START_REGISTRATION}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            fontSize: '0.9375rem',
                            fontWeight: 700,
                            color: PURPLE,
                            textDecoration: 'none',
                            '&:hover': { color: '#3F1C84' },
                        }}
                    >
                        {t('home.scale.cta_generate')}
                        <ArrowIcon />
                    </Box>
                </Stack>
            </Container>
        </Box>
    );
}
